import React, { FC, useContext, useMemo } from 'react';
import { Creditor, USAddress } from '@typings/api-models';
import Spacer from '@components/Spacer';

import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import Header from '@components/DesignLibrary/Header';
import dashboardContext from '@layouts/DashboardLayout/DashboardContext';
import { ListAndDetailsSubLayoutContext } from '@layouts/ListAndDetailsSubLayout';
import { useRouter } from 'next/router';
import { getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import TrashSimple from '@components/Icons/TrashSimple';
import ProgressIndicator from '@components/ProgressIndicator';
import { useToast } from '@/hooks';
import Button from '@/components/DesignLibrary/Button';
import { AddressUS, Input } from '@/components/DesignLibrary/Atoms';

import s from './CreateEditCreditorForm.module.scss';

type Props = {
  creditor: Creditor | null;
  onSubmit?: (creditorId: string | null) => Promise<void>;
};

const CreateEditCreditorForm: FC<Props> = ({ onSubmit, creditor }) => {
  const router = useRouter();
  const { creator } = useContext(dashboardContext);
  const { reloadListView } = useContext(ListAndDetailsSubLayoutContext);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [creditorName, setCreditorName] = React.useState<string | null>(creditor?.name ?? null);
  const [attention, setAttention] = React.useState<string | null>(creditor?.attention ?? null);
  const [address, setAddress] = React.useState<USAddress | null>(creditor
    ? {
      city: creditor.address.city,
      line1: creditor.address.line1,
      line2: creditor.address.line2,
      state: creditor.address.state,
      zip: creditor.address.zip,
    }
    : null,
  );

  const { getData: createCreditor } = useNoodleApi(tsClient.creators.createCreatorCreditor, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Creditor created successfully!'],
  });
  const { getData: updateCreditor } = useNoodleApi(tsClient.creators.updateCreatorCreditor, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Creditor updated successfully!'],
  });
  const { getData: deleteCreditorFn } = useNoodleApi(tsClient.creators.deleteCreatorCreditor, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Creditor deleted successfully!'],
  });

  const nameError: string | undefined = useMemo(() => {
    if (!creditorName) {
      return 'Required';
    }

    return undefined;
  }, [creditorName]);

  const addressError: string | undefined = useMemo(() => {
    if (!address) {
      return 'Address Required';
    }

    if (!address.line1) {
      return 'Address Line 1 Required';
    }

    if (!address.city) {
      return 'City Required';
    }

    if (!address.state) {
      return 'State Required';
    }

    if (!address.zip) {
      return 'Zip Required';
    }

    return undefined;
  }, [address]);

  const handleSave = async (): Promise<void> => {
    if (!creator) {
      return;
    }

    if (!address || nameError || addressError || !creditorName) {
      return;
    }

    const creditorData = {
      address,
      attention,
      name: creditorName,
    };

    let creditorId: string | null = null;
    if (creditor) {
      const updateData = await updateCreditor({
        creatorId: creator.id,
        ...creditorData,
        creditorId: creditor.id,
      });
      creditorId = updateData.data?.id || null;
      reloadListView();
    } else {
      const createData = await createCreditor({
        creatorId: creator.id,
        ...creditorData,
      });
      creditorId = createData.data?.id || null;
    }
    await onSubmit?.(creditorId);
  };

  const deleteCreditor = async (): Promise<void> => {
    if (creditor && creator) {
      setIsDeleting(true);
      await deleteCreditorFn({ creatorId: creator.id, creditorId: creditor.id });
      await reloadListView();
      await router.push(getUrl(IDENTIFIERS.DASHBOARD_CREDITORS));
      setIsDeleting(false);
    }
  };

  return (
    <>
      {creditor
        ? (
          <>
            <Header
              secondaryActions={[
                {
                  icon: TrashSimple,
                  isDestructive: true,
                  isFetching: isDeleting,
                  label: 'Delete',
                  onClick: deleteCreditor,
                },
              ]}
              title={creditorName || 'Creditor'}
              noDivider
            />
          </>
        )
        : <Spacer size={8} />}
      {isDeleting && (
        <>
          <Spacer size={16} />
          <ProgressIndicator isCentered />
        </>
      )}
      <div className={s.context}>
        <Spacer size={12}/>
        <Input
          placeholder="Enter value"
          value={creditorName}
          compact
          label='Creditor Name'
          onChange={setCreditorName}
          error={nameError}
        />
        <Spacer size={12}/>
        <Input
          placeholder="Enter value"
          value={attention}
          compact
          label='Attention'
          optional
          onChange={setAttention}
        />
        <Spacer size={12}/>
        <AddressUS
          label="Creditor Address"
          required={true}
          value={address}
          onChange={setAddress}
          error={addressError}
        />
        <Spacer size={24}/>
        <Button size="md" variant="primary" fullWidth onClick={handleSave}>Submit</Button>
      </div>
    </>
  );
};

export default CreateEditCreditorForm;
